import { _notNil } from '@/littledash';
import type { FC } from 'react';
import styles from './SendResults.module.scss';

export interface SendFilterChipOption {
  key: string;
  label: string;
  /** The server's count for the chip, when it publishes one that counts only what the chip filters. */
  count?: number;
}

interface SendFilterChipsProps {
  /** What the chips filter, read to assistive technology as the group's name. */
  label: string;
  options: ReadonlyArray<SendFilterChipOption>;
  /** The keys switched on. None switched on shows everything. */
  selected: ReadonlySet<string>;
  onToggle: (key: string) => void;
  testId: string;
}

/**
 * Toggle chips that narrow a SEND findings list. A chip carries a number only when the caller has the
 * server's count for exactly what it filters; a count is never recomputed from the findings on screen,
 * which a limit may have cut.
 */
const SendFilterChips: FC<SendFilterChipsProps> = ({ label, options, selected, onToggle, testId }) => (
  <div
    className={styles.filterChips}
    role="group"
    aria-label={label}
    data-test-component="SendFilterChips"
    data-testid={testId}
  >
    {options.map((option) => {
      const isOn = selected.has(option.key);
      return (
        <button
          key={option.key}
          type="button"
          className={`${styles.filterChip} ${isOn ? styles.filterChipOn : ''}`}
          aria-pressed={isOn}
          onClick={() => onToggle(option.key)}
          data-test-element="filter-chip"
          data-test-key={option.key}
        >
          {option.label}
          {_notNil(option.count) && (
            <span className={styles.filterChipCount} data-test-element="filter-chip-count">
              {option.count.toLocaleString('en-US')}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default SendFilterChips;
